import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ChatBox from "./chatBox/ChatBox";
import { Icon } from "@zorni/components-lib";
import styles from "./Messenger.module.css";
import { useZorni } from "../../../hooks/useZorni";
import { useTranslation } from "react-i18next";

import { useConversations } from "./hooks/useConversations";
import { useSendMessage } from "./hooks/useSendMessage";
import { useTripPublicationSummary } from "../../tripDetails/detailsTrip/hooks/useTripPublicationSummary";
import { USER_TYPE } from "./hooks/userTypeMapper";
import { useConversationWithMessages } from "./hooks/useConversationWithMessages";
import { useNotifications } from "../../../hooks/NotificationContext";
import { useOperation } from "../../../../dashboard/hooks/useOperation";

const getConversationLabel = (conversation) =>
  conversation?.establishmentId ||
  conversation?.tripReference ||
  conversation?.customerId ||
  "Conversation";

const Messenger = () => {
  const { t } = useTranslation();
  const { showToast } = useOperation();
  const navigate = useNavigate();
  const { conversationId } = useParams();
  const { userId, type } = useZorni();
  const { hasNewMessages, clearMessages } = useNotifications();
  const [searchQuery, setSearchQuery] = useState("");

  // 🔹 Un agent (PRO) voit les conversations qui lui sont assignées ;
  // un client (PART) voit ses propres conversations.
  const assignedAgentId = type === USER_TYPE.PRO ? userId : undefined;
  const customerId = type === USER_TYPE.PRO ? undefined : userId;

  // 🔹 Liste des conversations
  const {
    conversations,
    loading: loadingList,
    error: errorList,
    refetch: refetchConversations,
    loadMoreConversations,
    hasMoreConversations,
  } = useConversations(
    { currentPage: 0, sizePage: 10 },
    undefined,
    customerId,
    assignedAgentId
  );

  // 🔹 Conversation active avec pagination
  const {
    conversation,
    messages,
    error: errorConversation,
    refetchAll,
    loadMoreMessages,
    currentPage,
    totalPages,
  } = useConversationWithMessages(conversationId, {
    currentPage: 0,
    sizePage: 20,
  });

  const { sendMessage, loading: sendingMessage } = useSendMessage();
  const { tripPublicationSummary } = useTripPublicationSummary(
    conversation?.tripPublicationId
  );

  // 🧭 Redirige vers la première conversation si aucune sélectionnée
  useEffect(() => {
    if (!conversationId && conversations?.length > 0) {
      navigate(`/messenger/${conversations[0].id}`);
    }
  }, [conversationId, conversations, navigate]);

  // 🔄 Rafraîchissement quand un nouveau message arrive
  useEffect(() => {
    if (hasNewMessages) {
      refetchConversations();
      refetchAll();
      clearMessages();
    }
  }, [hasNewMessages, refetchConversations, refetchAll, clearMessages]);

  const handleSendMessage = async (reply) => {
    if (!reply.trim() || !conversation) return;

    const receiverId =
      type === USER_TYPE.PRO
        ? conversation.customerId
        : conversation.assignedAgentId;

    const newMsg = {
      conversationId: conversation.id,
      receiverId,
      content: reply,
      sendingDate: new Date().toISOString(),
    };

    try {
      await sendMessage(newMsg);
      refetchConversations();
      refetchAll();
    } catch (err) {
      console.error("Erreur lors de l'envoi du message :", err);
      showToast(
        t("messenger.sendError", "Votre message n'a pas pu être envoyé."),
        "error"
      );
    }
  };

  const filteredConversations = searchQuery.trim()
    ? conversations.filter((conv) =>
        getConversationLabel(conv)
          .toLowerCase()
          .includes(searchQuery.toLowerCase())
      )
    : conversations;

  const tripImage = tripPublicationSummary?.picture || null;
  const tripRef = tripPublicationSummary?.reference || null;

  return (
    <div className={styles.page}>
      <div className={styles.layout}>

        {/* ─── Sidebar gauche — liste des conversations ─── */}
        <aside className={styles.sidebar}>
          <div className={styles.sidebarHeader}>
            <h1 className={styles.sidebarTitle}>Messages</h1>
            <div className={styles.searchWrap}>
              <Icon name="iconSearch" size="xs" className={styles.searchIcon} />
              <input
                type="text"
                placeholder="Rechercher..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={styles.searchInput}
              />
            </div>
          </div>

          {errorList && (
            <div className={styles.panelMessage}>
              {errorList.message || "Impossible de charger les conversations."}
            </div>
          )}
          {loadingList && (
            <p className={styles.smallLoader}>Chargement...</p>
          )}
          {!loadingList && !errorList && !filteredConversations.length && (
            <div className={styles.panelMessage}>Aucune conversation.</div>
          )}

          <div className={styles.convList}>
            {filteredConversations.map((conv) => (
              <div
                key={conv.id}
                className={`${styles.conversationPreview} ${
                  conv.id === conversation?.id ? styles.active : ""
                } ${conv.lastMessage?.status === "UNREAD" ? styles.unread : ""}`}
                onClick={() => navigate(`/messenger/${conv.id}`)}
              >
                <div className={styles.avatarSmall}>
                  <span>{getConversationLabel(conv).slice(0, 2).toUpperCase()}</span>
                </div>
                <div className={styles.conversationBody}>
                  <div className={styles.convMeta}>
                    <strong className={styles.conversationName}>
                      {getConversationLabel(conv)}
                    </strong>
                    {conv?.lastMessage?.sendingDate && (
                      <small className={styles.timeStamp}>
                        {new Date(conv.lastMessage.sendingDate).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </small>
                    )}
                  </div>
                  <p className={styles.lastMessage}>
                    {conv.lastMessage?.content?.slice(0, 40) || "Aucun message"}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {hasMoreConversations && (
            <button onClick={loadMoreConversations} className={styles.loadMoreBtn}>
              Charger plus
            </button>
          )}
        </aside>

        {/* ─── Zone centrale — chat ─── */}
        {errorConversation ? (
          <div className={styles.emptyPanel}>
            <div className={styles.emptyIcon}>
              <Icon name="iconEnvelope" size="md" />
            </div>
            <p className={styles.emptyTitle}>Conversation indisponible</p>
            <p className={styles.emptyText}>
              {errorConversation.message || "Impossible de charger cette conversation."}
            </p>
          </div>
        ) : conversation ? (
          <ChatBox
            conversation={conversation}
            conversationLabel={getConversationLabel(conversation)}
            messages={messages}
            currentUserId={userId}
            onSendMessage={handleSendMessage}
            isSending={sendingMessage}
            loadMoreMessages={loadMoreMessages}
            currentPage={currentPage}
            totalPages={totalPages}
            onBack={() => navigate("/messenger")}
          />
        ) : (
          <div className={styles.emptyPanel}>
            <div className={styles.emptyIcon}>
              <Icon name="iconEnvelope" size="md" />
            </div>
            <p className={styles.emptyTitle}>Messagerie</p>
            <p className={styles.emptyText}>
              Sélectionnez une conversation dans la liste pour afficher les messages.
            </p>
          </div>
        )}

        {/* ─── Sidebar droite — détails voyage ─── */}
        <aside className={styles.tripDetails}>
          <p className={styles.panelEyebrow}>Détails Voyage</p>

          {tripPublicationSummary ? (
            <div className={styles.tripCard}>
              <div className={styles.tripImageWrap}>
                {tripImage ? (
                  <img
                    src={tripImage}
                    alt={tripPublicationSummary.title || "Voyage"}
                    className={styles.tripImage}
                  />
                ) : (
                  <div className={styles.tripImagePlaceholder}>
                    <Icon name="iconPlaneDeparture" size="md" />
                  </div>
                )}
                {tripRef && (
                  <span className={styles.tripRef}>{tripRef}</span>
                )}
              </div>
              <h4 className={styles.tripTitle}>{tripPublicationSummary.title}</h4>
              <button
                className={styles.tripBtn}
                onClick={() =>
                  navigate(
                    `/trips/${tripPublicationSummary.id}/${tripPublicationSummary.tripDateId}`
                  )
                }
              >
                Voir le voyage
              </button>
            </div>
          ) : (
            <div className={styles.panelMessage}>
              {conversation ? "Chargement du voyage..." : "Aucun voyage rattaché."}
            </div>
          )}
        </aside>

      </div>
    </div>
  );
};

export default Messenger;
